'use client';

import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command'; 
import { cn } from '@/lib/utils';
import { TypesearchSelected } from '../common/alldoctors/types/TypeAllDoctors.type';
import { FaTrashAlt } from 'react-icons/fa';
import { useVirtualizer } from '@tanstack/react-virtual';

function ComboboxDemo({
    label,
    data = [{ id: 0, name: 'موردی وجود ندارد' }],
    nameAction,
    selectedItem,
    onSelect, 
    labelStyles,
    btnStyles,
}: TypesearchSelected) {
    const [open, setOpen] = React.useState(false);
    const [scrollElement, setScrollElement] =
        React.useState<HTMLDivElement | null>(null);
    const [search, setSearch] = React.useState('');

    const handleSelect = (id: number | null) => {
        onSelect?.(selectedItem === id ? null : id);
        setOpen(false);
    };

    const getSelectedLabel = () => {
        const selected = data.find((item) => item.id === selectedItem);
        return selected?.name || selected?.title || nameAction;
    };

    // فیلتر کردن دیتا بر اساس سرچ
    const filteredData = React.useMemo(() => {
        if (!search) return data;
        const lower = search.toLowerCase();
        return data.filter((item) =>
            (item.name || item.title || '')
                .toString()
                .toLowerCase()
                .includes(lower),
        );
    }, [data, search]);

    // Virtualizer برای دیتا فیلترشده
    const virtualizer = useVirtualizer({
        count: filteredData.length,
        getScrollElement: () => scrollElement,
        estimateSize: () => 30,
        overscan: 100,
    });

    return (
        <div className="flex flex-col px-2 pb-3" dir="ltr">
            {label && (
                <p 
                    className={cn('w-full mb-3 text-gray-400 text-right', labelStyles)}
                >
                    {label}
                </p>
            )}

            <div className="flex items-center gap-3 w-full flex-grow">
                {selectedItem !== null && (
                    <Button
                        variant="outline"
                        className="bg-blue hover:bg-blue hover:text-white disabled:bg-gray-100 py-1 text-white transition-opacity"
                        onClick={() => handleSelect(selectedItem)}
                    >
                        <FaTrashAlt className="size-4" />
                    </Button>
                )}

                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            variant="outline"
                            role="combobox"
                            dir="rtl"
                            aria-expanded={open}
                            onClick={() => setOpen((prev) => !prev)}
                            className={cn(
                                'w-full justify-between hover:bg-white hover:text-black text-xs',
                                btnStyles,
                            )}
                        >
                            <span className="truncate max-w-[230px] sm:max-w-[230px]">
                                {getSelectedLabel()}
                            </span>
                            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                    </PopoverTrigger>

                    <PopoverContent dir="ltr" className="p-0">
                        <Command shouldFilter={false}>
                            <CommandInput
                                dir="rtl"
                                placeholder={nameAction}
                                value={search}
                                onValueChange={setSearch}
                            />
                            <CommandList
                                ref={(el) => {
                                    if (el) {
                                        setScrollElement(el);
                                    }
                                }}
                                className="h-[11rem] w-full overflow-y-auto"
                            >
                                <CommandEmpty>موردی یافت نشد!</CommandEmpty>
                                <CommandGroup
                                    className={cn('relative')}
                                    style={{
                                        height: `${
                                            virtualizer.getTotalSize() + 10
                                        }px`,
                                    }}
                                >
                                    {virtualizer
                                        .getVirtualItems()
                                        .map((virtualRow) => {
                                            const item =
                                                filteredData[virtualRow.index];
                                            return (
                                                <CommandItem
                                                    key={virtualRow.key}
                                                    data-index={
                                                        virtualRow.index
                                                    }
                                                    ref={
                                                        virtualizer.measureElement
                                                    }
                                                    style={{
                                                        transform: `translateY(${virtualRow.start}px)`,
                                                    }}
                                                    onSelect={() =>
                                                        handleSelect(item.id)
                                                    }
                                                    className="my-1 cursor-pointer absolute w-full ps-2 text-text_dark"
                                                    aria-selected={
                                                        selectedItem === item.id
                                                    }
                                                    dir="rtl"
                                                >
                                                    <span>
                                                        {item.name} {item.title}
                                                    </span>
                                                    {selectedItem ===
                                                        item.id && (
                                                        <Check className="ms-auto h-4 w-4" />
                                                    )}
                                                </CommandItem>
                                            );
                                        })}
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>
            </div>
        </div>
    );
}

export default ComboboxDemo;
